import React from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import { isFunction, isNil, uniq } from 'lodash'

import { chartColors } from '@/utils/constants'
import { createTrend } from '@/utils/helpers'
import { nativeTooltip } from '@/components/charts/helpers'

// The chart wrappers accept the nivo prop shapes their 18 consumers already
// pass (data/keys/indexBy, axisBottom/axisLeft, tooltip render functions) and
// translate them to an echarts option, so the nivo -> echarts migration never
// touched the consumers.

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] // prettier-ignore

// Subset of the d3-time-format patterns consumers pass (e.g. '%b %d').
export const formatTimePattern = (pattern, value) => {
  const date = new Date(value)
  const tokens = {
    '%Y': String(date.getFullYear()),
    '%y': String(date.getFullYear()).slice(-2),
    '%m': String(date.getMonth() + 1).padStart(2, '0'),
    '%b': MONTH_LABELS[date.getMonth()],
    '%d': String(date.getDate()).padStart(2, '0'),
  }
  return pattern.replace(/%[Yymbd]/g, (token) => tokens[token])
}

// nivo hides an axis entirely when the config is null.
export const nivoAxisToEchartsAxis = (axis, { time = false } = {}) => {
  if (isNil(axis)) {
    return { axisLabel: { show: false }, axisTick: { show: false }, axisLine: { show: false } }
  }

  const axisLabel = { show: true }
  if (!isNil(axis.tickPadding)) {
    axisLabel.margin = axis.tickPadding
  }
  if (!isNil(axis.tickRotation) && axis.tickRotation !== 0) {
    axisLabel.rotate = -axis.tickRotation
  }
  if (isFunction(axis.format)) {
    axisLabel.formatter = axis.format
  } else if (typeof axis.format === 'string' && time) {
    axisLabel.formatter = (value) => formatTimePattern(axis.format, value)
  }
  // Category-axis tickValues arrays list the only labels to show; time-axis
  // interval strings ('every day') fall through to echarts' auto spacing.
  if (Array.isArray(axis.tickValues)) {
    axisLabel.interval = (index, value) => axis.tickValues.includes(value)
  }

  const result = {
    axisLabel,
    axisTick: { show: (axis.tickSize ?? 5) !== 0, length: axis.tickSize ?? 5 },
  }
  // No nivo equivalent: a count axis passes minInterval: 1 so echarts can't
  // tick a small range in halves ("0.5 calls").
  if (!isNil(axis.minInterval)) {
    result.minInterval = axis.minInterval
  }
  if (axis.legend) {
    result.name = axis.legend
    result.nameLocation = 'middle'
    result.nameGap = Math.abs(axis.legendOffset ?? 30)
  }
  return result
}

const marginToGrid = (margin) => ({
  top: margin.top,
  right: margin.right,
  bottom: margin.bottom,
  left: margin.left,
})

// The custom tooltips are self-styled (WidgetTooltip cards), so the echarts
// chrome must be fully transparent around the rendered markup. echarts' host
// div defaults to white-space: nowrap, which would keep long labels from
// wrapping inside the card's max-width.
const customTooltip = (renderItem) => ({
  trigger: 'item',
  backgroundColor: 'transparent',
  borderWidth: 0,
  padding: 0,
  extraCssText: 'box-shadow: none; white-space: normal;',
  formatter: (params) => renderToStaticMarkup(<>{renderItem(params)}</>),
})

export const buildBarOption = ({
  data = [],
  keys = [],
  indexBy,
  layout = 'vertical',
  colors = chartColors,
  colorBy = 'index',
  groupMode = 'stacked',
  axisBottom = {},
  axisLeft = null,
  enableGridX = false,
  enableGridY = false,
  margin = { top: 50, right: 50, bottom: 50, left: 70 },
  padding = 0.3,
  borderRadius = 0,
  tooltip,
}) => {
  const horizontal = layout === 'horizontal'
  const resolveColor = (key, keyIndex, datum, datumIndex) => {
    if (isFunction(colors)) {
      return colors({ id: key, data: datum, indexValue: datum[indexBy], value: datum[key] })
    }
    return colors[(colorBy === 'id' ? keyIndex : datumIndex) % colors.length]
  }

  const series = keys.map((key, keyIndex) => ({
    name: key,
    type: 'bar',
    stack: keys.length > 1 && groupMode !== 'grouped' ? 'total' : undefined,
    barCategoryGap: `${padding * 100}%`,
    data: data.map((datum, datumIndex) => ({
      value: datum[key],
      original: datum,
      itemStyle: { color: resolveColor(key, keyIndex, datum, datumIndex), borderRadius },
    })),
  }))

  const categoryAxis = {
    type: 'category',
    data: data.map((datum) => String(datum[indexBy])),
    ...nivoAxisToEchartsAxis(horizontal ? axisLeft : axisBottom),
  }
  const valueAxis = { type: 'value', ...nivoAxisToEchartsAxis(horizontal ? axisBottom : axisLeft) }

  const xAxis = horizontal ? valueAxis : categoryAxis
  const yAxis = horizontal ? categoryAxis : valueAxis
  xAxis.splitLine = { show: enableGridX }
  yAxis.splitLine = { show: enableGridY }

  return {
    animation: false,
    grid: marginToGrid(margin),
    xAxis,
    yAxis,
    series,
    // Function tooltips expect nivo's bar item shape; plain objects pass
    // through as echarts tooltip config.
    tooltip: isFunction(tooltip)
      ? customTooltip((params) =>
          tooltip({
            id: params.seriesName,
            value: params.value,
            color: params.color,
            indexValue: params.name,
            data: params.data.original,
          })
        )
      : tooltip || nativeTooltip({ trigger: 'axis' }),
  }
}

export const buildLineOption = ({
  data = [],
  colors = chartColors,
  curve,
  pointSize = 8,
  enableArea = false,
  areaOpacity = 0.2,
  enableGridX = true,
  enableGridY = false,
  xScale = { type: 'point' },
  yScale = { type: 'linear', min: '0', max: 'auto' },
  axisBottom = {},
  axisLeft = null,
  margin = { top: 50, right: 50, bottom: 50, left: 70 },
  tooltip,
  trendlines = [],
}) => {
  const time = xScale.type === 'time'
  const scaleBound = (bound) => (isNil(bound) || bound === 'auto' ? undefined : Number(bound))

  const series = data.map((serie, serieIndex) => ({
    name: String(serie.id),
    type: 'line',
    smooth: curve === 'monotoneX' ? 0.4 : false,
    symbolSize: pointSize,
    // echarts' 'auto' default only draws symbols where an axis label is
    // shown, so sparse tickValues would hide most points; nivo drew them all.
    showAllSymbol: true,
    lineStyle: { width: 2 },
    itemStyle: { color: colors[serieIndex % colors.length] },
    areaStyle: enableArea ? { opacity: areaOpacity } : undefined,
    data: serie.data.map((point) => ({ value: [point.x, point.y], original: point })),
  }))

  // Replaces the nivo canvas-layer trend lines: a dashed least-squares
  // segment computed from the named serie's points.
  trendlines.forEach((trendline) => {
    const serie = data.find((candidate) => candidate.id === trendline.serieId)
    if (!serie || serie.data.length < 2) {
      return
    }
    // Some consumers use non-ISO x labels ('MM-DD'); regression falls back to
    // the point index, which is equivalent for evenly spaced points.
    const points = serie.data.map((point, pointIndex) => {
      const timestamp = new Date(point.x).getTime()
      return { x: Number.isNaN(timestamp) ? pointIndex : timestamp, y: point.y }
    })
    const trend = createTrend(points, 'x', 'y')
    const boundary = (sourceIndex) => [
      serie.data[sourceIndex].x,
      trend.calcY(points[sourceIndex].x),
    ]
    series.push({
      name: `${trendline.serieId} trend`,
      type: 'line',
      symbol: 'none',
      silent: true,
      lineStyle: { width: 2, type: 'dashed', color: trendline.color },
      tooltip: { show: false },
      data: [boundary(0), boundary(serie.data.length - 1)],
    })
  })

  const xAxis = {
    ...(time
      ? {
          // The axis tooltip's date header follows this formatter; echarts'
          // default prints midnight timestamps ('2026-06-30 00:00:00').
          type: 'time',
          axisPointer: {
            label: { formatter: ({ value }) => formatTimePattern('%m/%d/%Y', value) },
          },
        }
      : {
          type: 'category',
          boundaryGap: false,
          // Union across all series in first-seen order (nivo's point-scale
          // domain); points whose x is missing from the axis wouldn't render.
          data: uniq(data.flatMap((serie) => serie.data.map((point) => String(point.x)))),
        }),
    ...nivoAxisToEchartsAxis(axisBottom, { time }),
    splitLine: { show: enableGridX },
  }
  const yAxis = {
    type: 'value',
    min: scaleBound(yScale.min),
    max: scaleBound(yScale.max),
    ...nivoAxisToEchartsAxis(axisLeft),
    splitLine: { show: enableGridY },
  }

  return {
    // The nivo LineChart rendered with animate={false}, and EChart's
    // notMerge updates would replay the entry animation on every data change.
    animation: false,
    grid: marginToGrid(margin),
    xAxis,
    yAxis,
    series,
    // Function tooltips expect nivo's { point } shape; plain objects pass
    // through as echarts tooltip config.
    tooltip: isFunction(tooltip)
      ? customTooltip((params) =>
          tooltip({
            point: {
              serieId: params.seriesName,
              color: params.color,
              data: params.data.original,
            },
          })
        )
      : tooltip || nativeTooltip({ trigger: 'axis' }),
  }
}

export const buildBulletOption = ({ data = [], rangeColors, measureColors, margin }) => {
  // nivo lists the first row at the top; echarts category y-axes start at the
  // bottom, so reverse to preserve order.
  const rows = [...data].reverse()

  // Each range segment is one stacked series slice sized by the distance to
  // the previous boundary.
  const segmentCount = Math.max(...rows.map((row) => row.ranges.length - 1), 0)
  const rangeSeries = Array.from({ length: segmentCount }, (unused, segmentIndex) => ({
    name: `range-${segmentIndex}`,
    type: 'bar',
    stack: 'ranges',
    barWidth: '60%',
    silent: true,
    data: rows.map((row) => ({
      value: (row.ranges[segmentIndex + 1] ?? row.ranges[segmentIndex]) - row.ranges[segmentIndex],
      itemStyle: { color: rangeColors[segmentIndex % rangeColors.length] },
    })),
  }))

  const measureSeries = {
    name: 'measure',
    type: 'bar',
    barWidth: '12%',
    barGap: '-60%',
    z: 10,
    data: rows.map((row) => ({
      value: row.measures[0],
      itemStyle: { color: measureColors[0] },
    })),
  }

  return {
    grid: marginToGrid(margin),
    xAxis: { type: 'value', max: Math.max(...rows.flatMap((row) => row.ranges), 100) },
    yAxis: { type: 'category', data: rows.map((row) => String(row.id)) },
    series: [...rangeSeries, measureSeries],
    tooltip: nativeTooltip({ trigger: 'item' }),
  }
}
